import { MemoizedTable } from 'components/data_table/Table';
import { TableProps } from 'components/data_table/types';
import { PagedDataSource } from 'custom_hooks/data_hooks/data_source/usePagedDataSource';
import * as React from 'react';
import { Modify } from 'utils/types';

export type DataSourceTableProps<TData, TFilter> = Modify<
    Pick<TableProps<TData>, 'rowKey' | 'columns' | 'tableStyle' | 'tableClassName'>,
    {
        /**
         * Data Source of the table
         */
        dataSource: PagedDataSource<TData, TFilter>;

        /**
         * If provided, will render the table as loading, even if the
         * `dataSource` has loaded
         */
        isLoadingOverride?: boolean;

        /**
         * No result message
         */
        noResultMessage: React.ReactNode;
    }
>;

export function DataSourceTable<TData, TFilter>({
    dataSource,
    columns,
    rowKey,
    tableStyle,
    noResultMessage,
    tableClassName,
    isLoadingOverride,
    ...rest
}: DataSourceTableProps<TData, TFilter>) {
    const { isLoading, results, sort, setSort, paging, setPage } = dataSource;
    return !isLoading && results?.length === 0 ? (
        <span>{noResultMessage}</span>
    ) : (
        <MemoizedTable
            {...rest}
            isLoading={isLoadingOverride || isLoading}
            lockHeaders
            maxHeight="100rem"
            tableStyle={tableStyle}
            data={results}
            rowKey={rowKey}
            sort={sort}
            onSortChange={setSort}
            paging={paging}
            onPageChange={setPage}
            showPageSizeDropdown
            columns={columns}
            tableClassName={tableClassName}
        />
    );
}
